'use client';

import { c as _c } from "react/compiler-runtime";
import { ButtonGroup, Toggle } from '@repo/ui/components';
import { useColumnItemsShown } from './context/column-settings.ts';
import { ITEMS_SHOWN_CHOICES, type ItemsShownCount } from './context/merge-table-settings.ts';
import { useTableState } from './context/table-state-provider.tsx';
type ItemsShownChoices = {
  columnId: string;
  count: ItemsShownCount;
};

/** A List cell's Items shown; none for another column. */
export const useItemsShownChoices = columnId => {
  const $ = _c(4);
  const {
    count,
    isListCell
  } = useColumnItemsShown(columnId);
  let t0;
  if ($[0] !== columnId || $[1] !== count || $[2] !== isListCell) {
    t0 = isListCell ? {
      columnId,
      count
    } : undefined;
    $[0] = columnId;
    $[1] = count;
    $[2] = isListCell;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
};
export const formatItemsShown = (count: ItemsShownCount) => count === 1 ? '1 item' : `${count} items`;

/** A sketch of the lines a List cell's items take: one line, or two. */
const ItemsGlyph = t0 => {
  const $ = _c(5);
  const {
    count
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="h-0.5 w-full rounded-full bg-current" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== count) {
    t2 = count === 2 && <span className="h-0.5 w-full rounded-full bg-current" />;
    $[1] = count;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <span aria-hidden={true} className="flex w-4 flex-col gap-0.5" data-sentry-component="ItemsGlyph" data-sentry-source-file="settings-items-shown.tsx">{t1}{t2}</span>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};

/** One choice of 1 or 2 items, beside its label, which says it changes Comfortable rows only. */
export const ItemsShownPicker = t0 => {
  const $ = _c(12);
  const {
    columnId,
    count
  } = t0;
  const {
    setItemsShown
  } = useTableState();
  const labelId = `table-settings-items-shown-${columnId}`;
  let t1;
  if ($[0] !== labelId) {
    t1 = <span className="min-w-0 flex-1 truncate text-sm" id={labelId}>Items in Comfortable rows</span>;
    $[0] = labelId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== columnId || $[3] !== count || $[4] !== setItemsShown) {
    t2 = ITEMS_SHOWN_CHOICES.map(choice => <Toggle className="h-7 gap-1.5 px-2 text-xs" key={choice} onPressedChange={pressed => pressed && setItemsShown(columnId, choice)} pressed={choice === count} size="sm" variant="outline"><ItemsGlyph count={choice} />{formatItemsShown(choice)}</Toggle>);
    $[2] = columnId;
    $[3] = count;
    $[4] = setItemsShown;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== labelId || $[7] !== t2) {
    t3 = <ButtonGroup aria-labelledby={labelId} data-sentry-element="ButtonGroup" data-sentry-source-file="settings-items-shown.tsx">{t2}</ButtonGroup>;
    $[6] = labelId;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3) {
    t4 = <div className="flex items-center gap-2 px-1 py-1" data-sentry-component="ItemsShownPicker" data-sentry-source-file="settings-items-shown.tsx">{t1}{t3}</div>;
    $[9] = t1;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
};